import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, auth, inputCls } from '../finance/shared';
import { EmptyState } from '../../../components/dashboard/TodaySchedule';
import Icon from '../../../components/dashboard/DashboardIcons';
import { WhatsAppMessageModal } from '../../../components/whatsapp/WhatsAppMessageModal';

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('ar-SA'); } catch { return iso; }
};

// «رسالة جديدة»: اختيار لاعب (ولي أمره) أو موظف، ثم فتح نافذة الرسالة
export const NewMessageTab = () => {
  const [recipientKind, setRecipientKind] = useState('player');
  const [players, setPlayers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/players`, auth()).catch(() => ({ data: [] })),
      axios.get(`${API}/employees`, auth()).catch(() => ({ data: [] })),
    ]).then(([pRes, eRes]) => {
      setPlayers(Array.isArray(pRes.data) ? pRes.data : []);
      setEmployees(Array.isArray(eRes.data) ? eRes.data : []);
    }).finally(() => setLoading(false));
  }, []);

  const list = recipientKind === 'player' ? players : employees;
  const filtered = list.filter((item) => {
    const name = recipientKind === 'player' ? (item.guardian_name || item.name) : item.full_name;
    return !search || (name || '').toLowerCase().includes(search.toLowerCase());
  });

  const openFor = (item) => {
    setSelected(item);
    setModalOpen(true);
  };

  const variablesFor = (item) => {
    if (recipientKind === 'player') {
      const remaining = (item.subscription_amount ?? null) !== null && (item.subscription_paid_amount ?? null) !== null
        ? Math.max(item.subscription_amount - item.subscription_paid_amount, 0) : null;
      return {
        guardian_name: item.guardian_name || 'ولي الأمر',
        player_name: item.name,
        subscription_name: item.subscription_package || '',
        expiry_date: fmtDate(item.subscription_end),
        paid_amount: item.subscription_paid_amount ?? '',
        remaining_amount: remaining ?? '',
        payment_reference: item.subscription_payment_reference || '',
      };
    }
    return { guardian_name: item.full_name, player_name: '' };
  };

  const recipientFor = (item) => ({
    id: item.id,
    name: recipientKind === 'player' ? (item.guardian_name || item.name) : item.full_name,
    phone: recipientKind === 'player' ? item.guardian_phone : item.phone,
    type: recipientKind === 'player' ? 'player_guardian' : 'employee',
  });

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="new-message-tab">
      <div className="mb-4 flex gap-2">
        <button
          onClick={() => setRecipientKind('player')}
          data-testid="new-message-recipient-player-btn"
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${recipientKind === 'player' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'}`}
        >
          لاعب / ولي أمر
        </button>
        <button
          onClick={() => setRecipientKind('employee')}
          data-testid="new-message-recipient-employee-btn"
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${recipientKind === 'employee' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'}`}
        >
          موظف
        </button>
      </div>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="ابحث بالاسم..."
        data-testid="new-message-search-input"
        className={`${inputCls} mb-3`}
      />

      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />)}</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon="users" text="لا توجد نتائج" testId="new-message-empty" />
      ) : (
        <div className="space-y-1.5 max-h-[420px] overflow-y-auto" data-testid="new-message-recipient-list">
          {filtered.map((item) => {
            const r = recipientFor(item);
            return (
              <div key={item.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5 hover:bg-slate-50" data-testid="new-message-recipient-row">
                <div>
                  <p className="text-sm font-bold text-slate-800">{r.name || '—'}</p>
                  <p className="text-xs text-slate-400" dir="ltr">{r.phone || 'لا يوجد رقم'}</p>
                </div>
                <button
                  onClick={() => openFor(item)}
                  data-testid="new-message-open-modal-btn"
                  className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-100"
                >
                  <Icon name="whatsapp" className="w-3.5 h-3.5" /> مراسلة
                </button>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <WhatsAppMessageModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          recipient={recipientFor(selected)}
          variables={variablesFor(selected)}
          relatedType={recipientKind === 'player' ? 'player' : 'employee'}
          relatedId={selected.id}
        />
      )}
    </div>
  );
};
